import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import * as Scroll from 'react-scroll'
import PropTypes from 'prop-types'

import theme from 'theme'
import media from 'media'

import { getModeStyling } from './helpers'

import { MOBILE_PADDING, PADDING_X, PADDING_TOP } from './constants'
import {
  calculateLength,
  getThemeLength,
  splitLength,
  getPaddingValues,
} from 'helpers'
import useVignette, { TOP_RIGHT, BOTTOM_LEFT } from 'hooks/useVignette'

export const CLASS_NAME = 'anvil-subsection'

// for consumers to make subsections side by side
// tablet does NOT go side by side
export const SubSectionContainer = styled(Flex)`
  width: calc(100vw - var(--scrollbar-width));
  max-width: ${theme.paperMaxWidth}px;
`

// honestly, only useful within the 'PaperContainer' part of Sections
// TODO: have a normalizer so we can use '75px' and '75' interchangeably
const getContainerAttrs = ({
  theme,
  developerEnteredMobilePadding,
  developerEnteredPadding,
}) => {
  const mobilePadding = getThemeLength({
    theme,
    key: 'padding',
    component: 'section',
    breakpoint: 'mobile',
  })

  const {
    paddingRight: userMobilePaddingRight,
    paddingLeft: userMobilePaddingLeft,
    paddingTop: userMobilePaddingTop,
    paddingBottom: userMobilePaddingBottom,
  } = getPaddingValues(mobilePadding, MOBILE_PADDING)

  const mobilePaddingRight =
    developerEnteredMobilePadding?.right || userMobilePaddingRight
  const mobilePaddingLeft =
    developerEnteredMobilePadding?.left || userMobilePaddingLeft
  const mobilePaddingTop =
    developerEnteredMobilePadding?.top || userMobilePaddingTop
  const mobilePaddingBottom =
    developerEnteredMobilePadding?.bottom || userMobilePaddingBottom

  const tabletPadding = getThemeLength({
    theme,
    key: 'padding',
    component: 'section',
    breakpoint: 'tablet',
  })

  const {
    paddingRight: userTabletPaddingRight,
    paddingLeft: userTabletPaddingLeft,
    paddingTop: userTabletPaddingTop,
    paddingBottom: userTabletPaddingBottom,
  } = getPaddingValues(tabletPadding, MOBILE_PADDING)

  const tabletPaddingRight = userTabletPaddingRight
  const tabletPaddingLeft = userTabletPaddingLeft
  const tabletPaddingTop = userTabletPaddingTop
  const tabletPaddingBottom = userTabletPaddingBottom

  const desktopPadding = getThemeLength({
    theme,
    key: 'padding',
    component: 'section',
    breakpoint: 'desktop',
  })

  const {
    paddingRight: desktopUserPaddingRight,
    paddingLeft: desktopUserPaddingLeft,
    paddingTop: desktopUserPaddingTop,
    paddingBottom: desktopUserPaddingBottom,
  } = getPaddingValues(desktopPadding, {
    paddingX: PADDING_X,
    paddingY: PADDING_TOP,
  })

  const desktopPaddingRight = desktopUserPaddingRight
  const desktopPaddingLeft = desktopUserPaddingLeft
  const desktopPaddingTop = desktopUserPaddingTop
  const desktopPaddingBottom = desktopUserPaddingBottom

  const padding = getThemeLength({
    theme,
    key: 'padding',
    component: 'section',
  })

  const {
    paddingRight: userPaddingRight,
    paddingLeft: userPaddingLeft,
    paddingTop: userPaddingTop,
    paddingBottom: userPaddingBottom,
  } = getPaddingValues(padding, {
    paddingX: PADDING_X,
    paddingY: PADDING_TOP,
  })

  const paddingRight = developerEnteredPadding?.right || userPaddingRight
  const paddingLeft = developerEnteredPadding?.left || userPaddingLeft
  const paddingTop = developerEnteredPadding?.top || userPaddingTop
  const paddingBottom = developerEnteredPadding?.bottom || userPaddingBottom

  // NOTE: this component requires padding left and right to be the same
  // the logic below is to set a halfway decent width for the container
  // if they are not the same, we just set the width to 100vw.
  const mobileWidth =
    mobilePaddingLeft === mobilePaddingRight
      ? `calc(100% + ${calculateLength(mobilePaddingLeft, '*', 2)})`
      : '100vw'
  const tabletWidth =
    tabletPaddingLeft === tabletPaddingRight
      ? `calc(100% + ${calculateLength(tabletPaddingLeft, '*', 2)})`
      : '100vw'
  const desktopWidth =
    desktopPaddingLeft === desktopPaddingRight
      ? `calc(100% + ${calculateLength(desktopPaddingLeft, '*', 2)})`
      : '100vw'
  const width =
    paddingLeft === paddingRight
      ? `calc(100% + ${calculateLength(paddingLeft, '*', 2)})`
      : '100vw'

  return {
    width: [mobileWidth, tabletWidth, desktopWidth, width],
    mt: [
      mobilePaddingBottom,
      tabletPaddingBottom,
      desktopPaddingBottom,
      paddingBottom,
    ],
    mb: [
      calculateLength(mobilePaddingBottom, '*', -1),
      calculateLength(tabletPaddingBottom, '*', -1),
      calculateLength(desktopPaddingBottom, '*', -1),
      calculateLength(paddingBottom, '*', -1),
    ],
    pr: [
      mobilePaddingRight,
      tabletPaddingRight,
      desktopPaddingRight,
      paddingRight,
    ],
    pl: [
      mobilePaddingLeft,
      tabletPaddingRight,
      desktopPaddingLeft,
      paddingLeft,
    ],
    pt: [mobilePaddingTop, tabletPaddingTop, desktopPaddingTop, paddingTop],
    pb: [
      mobilePaddingBottom,
      tabletPaddingBottom,
      desktopPaddingBottom,
      paddingBottom,
    ],
    className: CLASS_NAME,
    as: Scroll.Element,
  }
}

const Container = styled(Box)
  .attrs(getContainerAttrs)
  .withConfig({
    shouldForwardProp: (prop) =>
      [
        'developerEnteredMobilePadding',
        'developerEnteredPadding',
        'mode',
        'background',
      ].indexOf(prop) === -1,
  })`
  max-width: ${theme.paperMaxWidth}px;

  ${({ mode, theme, background }) => getModeStyling({ mode, theme, background, isSubSection: true })}

  position: relative;
  left: -${({ theme }) => {
    const mobilePadding = getThemeLength({
      theme,
      key: 'padding',
      component: 'section',
      breakpoint: 'mobile',
    })

    return calculateLength(mobilePadding?.left, '*', 1) || `${MOBILE_PADDING}px`
  }};

  ${media.tablet`
      left: -${({ theme }) => {
        const padding = getThemeLength({
          theme,
          key: 'padding',
          component: 'section',
          breakpoint: 'tablet',
        })
        return calculateLength(padding?.left, '*', 1) || `${PADDING_X}px`
      }};
  `}

  ${media.desktop`
        left: -${({ theme }) => {
          const desktopPadding = getThemeLength({
            theme,
            key: 'padding',
            component: 'section',
            breakpoint: 'desktop',
          })
          return (
            calculateLength(desktopPadding?.left, '*', 1) || `${PADDING_X}px`
          )
        }};
    `}

  ${media.giant`
        left: -${({ theme }) => {
          const padding = getThemeLength({
            theme,
            key: 'padding',
            component: 'section',
          })
          return calculateLength(padding?.left, '*', 1) || `${PADDING_X}px`
        }};
    `}
`

export default function SubSection ({
  children,
  scrollName,
  vignette,
  mobilePadding,
  padding,
  ...others
}) {
  const developerEnteredMobilePadding = splitLength(mobilePadding)
  const developerEnteredPadding = splitLength(padding)

  const vignetteElement = useVignette(vignette)

  const as = scrollName ? Scroll.Element : 'div'
  return (
    <Container
      name={scrollName}
      as={as}
      developerEnteredMobilePadding={developerEnteredMobilePadding}
      developerEnteredPadding={developerEnteredPadding}
      {...others}
    >
      {children}
      {vignetteElement}
    </Container>
  )
}

SubSection.propTypes = {
  scrollName: PropTypes.string,
  children: PropTypes.node.isRequired,
  mode: PropTypes.oneOf(['light', 'dark', 'blue']),
  vignette: PropTypes.shape({
    position: PropTypes.oneOf([TOP_RIGHT, BOTTOM_LEFT]),
    color: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
  }),
}
